import { Button, FoodsIcon, FoodsIconButton, FoodsTooltip, Typography } from "@foodpilot/foods";
import { Dialog, DialogActions, DialogContent, DialogTitle, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CsrPillarForm } from "../EditPillarForm/PillarForm.utils";
import { PillarInfo } from "../EditPillarForm/Pages/PillarInfo";
import { canSavePillar } from "./UpsertDialog.utils";

export type PillarDialogState = { mode: "create"; pillar: CsrPillarForm } | { mode: "edit"; pillar: CsrPillarForm };

type PillarUpsertDialogProps = {
    open: boolean;
    state: PillarDialogState;
    isSaving: boolean;
    onClose(): void;
    onSave(pillar: CsrPillarForm): void;
};

export const PillarUpsertDialog = (props: PillarUpsertDialogProps) => {
    const { open, state, isSaving, onClose, onSave } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [form, setForm] = useState(state.pillar);

    useEffect(() => {
        if (open) setForm(state.pillar);
    }, [open, state]);

    const isCreate = state.mode === "create";
    const canSave = canSavePillar(form);
    const closeDialog = () => {
        if (!isSaving) onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={closeDialog}
            fullWidth
            maxWidth="md"
            aria-labelledby="pillar-upsert-dialog-title"
            slotProps={{ paper: { sx: { borderRadius: 2, maxHeight: `calc(100% - ${theme.spacing(4)})` } } }}
        >
            <DialogTitle
                id="pillar-upsert-dialog-title"
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3.5, py: 2.5 }}
            >
                <Typography component="span" variant="big-bold">
                    {isCreate ?
                        t("settings.csr.pillar_dialog.create_title", "Add a pillar")
                    :   t("settings.csr.pillar_dialog.edit_title", "Edit a pillar")}
                </Typography>
                <FoodsIconButton
                    size="m"
                    icon="close"
                    buttonProps={{
                        "aria-label": t("settings.csr.pillar_dialog.close", "Close pillar dialog"),
                        disabled: isSaving,
                        onClick: closeDialog,
                    }}
                />
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                <PillarInfo form={form} setForm={setForm} dialogLayout />
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between", px: 3.5, py: 2.5 }}>
                <Button
                    variant="text"
                    endIcon={<FoodsIcon icon="close" size={1.5} />}
                    disabled={isSaving}
                    onClick={closeDialog}
                >
                    {t("primarydialog.cancel", "Cancel")}
                </Button>
                <FoodsTooltip
                    title={
                        canSave ? "" : (
                            t(
                                "settings.csr.pillar_dialog.required_fields_tooltip",
                                "All fields marked with * must be filled in order to save your pillar.",
                            )
                        )
                    }
                >
                    <span>
                        <Button
                            variant="primary"
                            startIcon={<FoodsIcon icon="check" size={2} />}
                            disabled={!canSave || isSaving}
                            onClick={() => onSave(form)}
                        >
                            {t("glossary.validate", "Validate")}
                        </Button>
                    </span>
                </FoodsTooltip>
            </DialogActions>
        </Dialog>
    );
};
